import { ContainerFlex } from '@app/components/Container/ContainerFlex'
import { IconClose } from '@app/components-legacy/Icon'
import { useStores } from '@app/hooks'
import { useDeviceViewPort } from '@app/hooks/useDeviceViewPort'
import { DeviceViewPortWidth } from '@app/hooks/useDeviceViewPort/types'
import { consts, helpers } from '@app/styles'
import { useDSTheme } from '@design-system/hooks/useDSTheme'
import { buildVariants } from '@design-system/libs/buildVariants'
import { autorun } from 'mobx'
import { observer } from 'mobx-react-lite'
import React from 'react'
import styled from 'styled-components'
import {
  handleAppRibbonTimer,
  handleWidgetRibbonVisibilityOnClick
} from './handlers'

export interface ILabelAppRibbonProps {
  labelledBy?: string
  variant: 'version' | 'license'
  closeNotAllowed?: boolean
  children: React.ReactNode
}

const RIBBON_WIDTH = '600px'

const StyledContainerFlex = styled(ContainerFlex)<
  Omit<ILabelAppRibbonProps, 'children'>
>(props => {
  const theme = useDSTheme()

  const tulHeight = theme.tokens['layout/tul/height']

  return buildVariants(props)
    .css({
      position: 'absolute',
      top: 0,
      left: '50%',
      zIndex: consts.zIndexLabelAppRibbon,
      marginLeft: `-${helpers.divide(RIBBON_WIDTH, 2)}`,
      marginTop: 10,
      padding: consts.paddingVeryVerySmall,
      color: 'white',
      textAlign: 'center',
      width: RIBBON_WIDTH,
      lineHeight: '16px',
      height: tulHeight - 20,
      borderRadius: consts.borderRadiusRedesign,

      a: {
        color: 'white',
        textDecoration: 'underline'
      }
    })

    .variant('variant', props.variant, {
      version: {
        backgroundColor: consts.colorPurple010,
        fontWeight: consts.fontWeightBold
      },

      license: {
        marginTop: 4,
        backgroundColor: consts.colorRed001,
        fontWeight: 'normal',
        height: helpers.sub(consts.headerHeight, 2)
      }
    })

    .end()
})

const StyledIconClose = styled(IconClose)(props => {
  return buildVariants(props)
    .css({
      position: 'absolute',
      right: '15px'
    })
    .end()
})

const LabelAppRibbon: React.FC<ILabelAppRibbonProps> = props => {
  const { deviceViewPort } = useDeviceViewPort()
  const { storeLicense } = useStores()

  // allow the user closing the ribbon if case of small resolution
  // because of overlapping header icons
  const closable =
    deviceViewPort.isLessThan(DeviceViewPortWidth.large) &&
    !props.closeNotAllowed

  React.useEffect(() => {
    if (!closable) {
      return
    }

    handleWidgetRibbonVisibilityOnClick(storeLicense, true)()
  }, [])

  /** When closed, wait for a given time before showing the ribbon again */
  React.useEffect(() => {
    if (!closable) {
      return
    }

    // for some reason, the React effect is not triggered with `storeLicense.isRibbonVisible` dependency,
    // so using a mobx observer here, then disposed at component unmount
    const disposer = autorun(() => {
      if (storeLicense.isRibbonVisible === false) {
        const { ribbonTimer } =
          storeLicense.storeRoot.environment.config.app.settings ?? 300000

        handleAppRibbonTimer(storeLicense, ribbonTimer)()
      }
    })

    return () => {
      disposer()
    }
  }, [])

  return (
    <StyledContainerFlex
      name="LabelAppRibbon"
      alignItems="center"
      justifyContent="center"
      labelledBy={props.labelledBy}
      variant={props.variant}
      items={[
        <div>{props.children}</div>,

        closable && (
          <StyledIconClose
            onClick={handleWidgetRibbonVisibilityOnClick(storeLicense, false)}
            color={consts.colorWhite}
          />
        )
      ]}
    />
  )
}

export default observer(LabelAppRibbon)
